import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, LoaderCircle, Trash2, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { PlanActionsBar } from '../components/PlanActionsBar';
import { PlanEventCard } from '../components/PlanEventCard';
import { PlanStatsCards } from '../components/PlanStatsCards';
import { PlanStatusStepper } from '../components/PlanStatusStepper';
import { PRsSection } from '../components/PRsSection';
import { usePrGenerationProgress } from '../hooks/usePrGenerationProgress';

/**
 * Map an MCP tool id to a shorter label for the activity feed. Only covers
 * the tools the instrumentation agent actually calls; everything else falls
 * back to the raw id (minus the mcp__*__ prefix).
 */
const TOOL_NAME_MAP: Record<string, string> = {
  mcp__github__clone_repo: 'clone repo',
  mcp__github__create_branch: 'create branch',
  mcp__github__commit_and_push: 'commit',
  mcp__github__create_pull_request: 'open PR',
  mcp__github__get_file_content: 'read file',
  mcp__shell__read: 'read',
  mcp__shell__grep: 'grep',
  mcp__shell__glob: 'glob',
  mcp__measurement__link_pr: 'link PR',
  mcp__measurement__get_measurement_plan: 'read plan',
  mcp__context__search_context: 'search context',
  mcp__context__get_context_item: 'read context',
  mcp__context__save_memory: 'save memory',
};

function friendlyToolName(toolName: string | undefined): string {
  if (!toolName) return '';
  return TOOL_NAME_MAP[toolName] ?? toolName.replace(/^mcp__[^_]+__/, '');
}

export function MeasurementPlanDetail(): React.ReactElement | null {
  const { id } = useParams<{ id: string }>();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { wp } = useWorkspace();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  const getQueryOptions = orpc.measurementPlan.get.queryOptions({
    input: { id: id ?? '' },
  });
  const listQueryOptions = orpc.measurementPlan.list.queryOptions({ input: {} });
  const { data, error, isLoading } = useQuery({
    ...getQueryOptions,
    refetchInterval: (query) => (query.state.data?.plan?.status === 'implementing' ? 5000 : false),
  });
  usePageTitle(data?.plan?.title);

  const approveMutation = useMutation(
    orpc.measurementPlan.updateStatus.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getQueryOptions.queryKey });
        void queryClient.invalidateQueries({ queryKey: listQueryOptions.queryKey });
      },
    }),
  );

  const revertMutation = useMutation(
    orpc.measurementPlan.updateStatus.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getQueryOptions.queryKey });
        void queryClient.invalidateQueries({ queryKey: listQueryOptions.queryKey });
      },
    }),
  );

  const verifyMutation = useMutation(
    orpc.measurementPlan.updateStatus.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getQueryOptions.queryKey });
        void queryClient.invalidateQueries({ queryKey: listQueryOptions.queryKey });
      },
    }),
  );

  const deleteMutation = useMutation(
    orpc.measurementPlan.delete.mutationOptions({
      onSuccess: () => {
        setShowDeleteDialog(false);
        void queryClient.invalidateQueries({ queryKey: listQueryOptions.queryKey });
        void navigate(wp('/plans'));
      },
    }),
  );

  const generateMutation = useMutation(
    orpc.measurementPlan.generatePrs.mutationOptions({
      onSuccess: () => {
        setShowGenerateDialog(false);
        void queryClient.invalidateQueries({ queryKey: getQueryOptions.queryKey });
        void queryClient.invalidateQueries({ queryKey: listQueryOptions.queryKey });
      },
    }),
  );

  // Subscribe to the plan-progress SSE stream while the agent is running.
  // The hook short-circuits when `enabled` is false and tears down cleanly
  // when the status transitions out of `implementing`.
  const isImplementing = data?.plan?.status === 'implementing';
  const {
    hasWarnings,
    mismatches,
    events: progressEvents,
  } = usePrGenerationProgress(data?.plan?.id ?? '', isImplementing);

  const stats = useMemo(() => {
    const events = data?.plan?.spec?.events ?? [];
    const sources = data?.plan?.spec?.sources ?? [];
    const prs = data?.prs ?? [];
    const propertyCount = events.reduce((sum, e) => sum + (e.properties?.length ?? 0), 0);
    return {
      eventCount: events.length,
      propertyCount,
      sourceCount: sources.length,
      prCount: prs.length,
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden">
        <div
          role="status"
          aria-label="Loading measurement plan"
          className="flex flex-1 items-center justify-center py-16"
        >
          <LoaderCircle className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex flex-1 flex-col items-center justify-center py-24 text-center">
          <h2 className="text-base font-medium text-foreground">Failed to load plan</h2>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            {error.message || 'An unexpected error occurred. Please try again.'}
          </p>
        </div>
      </div>
    );
  }

  if (!data?.plan) return null;

  const events = data.plan.spec?.events ?? [];

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="border-b px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-foreground">{data.plan.title}</h1>
            {data.plan.description && <p className="mt-0.5 text-sm text-muted-foreground">{data.plan.description}</p>}
          </div>
          {/* Top-right action bar — Delete lives here (not in the bottom action
              row) so the destructive action is visually separated from the
              workflow actions (Approve, Generate PRs, etc.). */}
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="text-muted-foreground hover:text-rose-600"
              data-testid="delete-plan-button"
            >
              <Trash2 size={14} className="mr-1" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      <div id="plan-scroll-container" className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl space-y-6 px-6 py-6">
          <PlanStatusStepper status={data.plan.status} />

          <PlanStatsCards {...stats} />
          {events.length > 0 && (
            <section id="events" className="space-y-4">
              {events.map((event) => (
                <PlanEventCard key={event.id} event={event} />
              ))}
            </section>
          )}

          {/* Progress + PRs live together at the bottom so the activity feed
              sits right above the linked PR rows it's generating. */}
          {isImplementing && (
            <div
              className="rounded-md border border-primary/30 bg-primary/5 p-4 text-sm"
              data-testid="implementing-banner"
            >
              <div className="flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                <span>The instrumentation agent is generating pull requests. This page will update automatically.</span>
              </div>
              {progressEvents.length > 0 && (
                <ul
                  className="mt-2 space-y-1 font-mono text-xs text-muted-foreground"
                  data-testid="progress-activity-feed"
                >
                  {progressEvents.slice(-8).map((e, i) => (
                    <li key={i} className="truncate">
                      <span className="text-foreground">{e.repo}</span>
                      <span className="mx-1 text-muted-foreground/60">·</span>
                      {e.status === 'tool_activity' ? (
                        <>
                          <span className="text-muted-foreground">{friendlyToolName(e.tool_name)}</span>
                          {e.tool_summary ? (
                            <span className="ml-1 text-muted-foreground/80">{e.tool_summary}</span>
                          ) : null}
                        </>
                      ) : (
                        <span>{e.status}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {hasWarnings && (
            <div
              className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-sm"
              data-testid="consistency-warnings-banner"
            >
              <div className="flex items-center gap-2 font-medium">
                <TriangleAlert className="size-4 text-amber-600" />
                {mismatches.length} consistency warning{mismatches.length === 1 ? '' : 's'}
              </div>
              <ul className="mt-2 space-y-1">
                {mismatches.map((m, i) => (
                  <li key={i}>
                    <strong>{m.event_name}</strong>
                    {m.property ? ` · ${m.property}` : ''}: {m.detail}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <section id="prs">
            <PRsSection prs={data.prs ?? []} />
          </section>
          <PlanActionsBar
            status={data.plan.status}
            onApprove={() => {
              approveMutation.mutate({ id: data.plan.id, status: 'approved' });
            }}
            onRevertToDraft={() => {
              revertMutation.mutate({ id: data.plan.id, status: 'draft' });
            }}
            onMarkVerified={() => {
              verifyMutation.mutate({ id: data.plan.id, status: 'verified' });
            }}
            onGeneratePrs={() => setShowGenerateDialog(true)}
            approvePending={approveMutation.isPending}
            revertPending={revertMutation.isPending}
            verifyPending={verifyMutation.isPending}
            generatePending={generateMutation.isPending}
          />
        </div>
      </div>

      {/* Delete confirmation — replaces the old inline two-step confirm */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-plan-dialog">
          <DialogHeader>
            <DialogTitle>Delete this measurement plan?</DialogTitle>
            <DialogDescription>
              This cannot be undone. Any linked pull requests will remain in GitHub but will no longer appear under this
              plan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate({ id: data.plan.id })}
              disabled={deleteMutation.isPending}
              data-testid="confirm-delete-plan"
            >
              {deleteMutation.isPending ? <LoaderCircle className="mr-1 size-4 animate-spin" /> : null}
              Delete plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate PRs confirmation — replaces window.confirm */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent data-testid="generate-prs-dialog">
          <DialogHeader>
            <DialogTitle>Generate pull requests?</DialogTitle>
            <DialogDescription>
              This will create a branch and open a pull request in each linked repository. You'll be able to follow
              progress on this page as the instrumentation agent runs.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowGenerateDialog(false)}
              disabled={generateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => generateMutation.mutate({ id: data.plan.id })}
              disabled={generateMutation.isPending}
              data-testid="confirm-generate-prs"
            >
              {generateMutation.isPending ? <LoaderCircle className="mr-1 size-4 animate-spin" /> : null}
              Generate PRs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
